import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Stack,
  Typography,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useState, useEffect } from "react";

import KsbDialogActions from "../../../2_ksbHub/components/KsbDialogActions";

import { useSondertagContext } from "../context/sondertagContext";
import appService from "../../../2_ksbHub/service/app.service";
import { useAppContext } from "../../../2_ksbHub/context/appContext";

export default function AngebotDialog({
  open,
  onClose,
  onSave,
  angebot,
  klasseId,
  halbtag,
  onDelete,
}) {
  const { isMobile, currentUser, isTeacherView } = useAppContext();

  const { isASondertageEventAdmin } = useSondertagContext();

  const isEditMode = !!angebot;

  const [teachers, setTeachers] = useState([]);

  const [title, setTitle] = useState("");
  const [specialTitle, setSpecialTitle] = useState("");
  const [owners, setOwners] = useState([]);

  const [error, setError] = useState("");

  const [reserveFullDay, setReserveFullDay] = useState(false);
  const [reserveFullWeek, setReserveFullWeek] = useState(false);

  /**
   * =========================================================
   * ✅ STATE LOGIC
   * =========================================================
   */

  const isReserved = angebot?.owners?.length > 0;

  const isOwner =
    angebot?.owners?.some((o) => o.id === currentUser?.id) ?? false;

  const isJoinMode =
    isReserved && !isOwner && (!isASondertageEventAdmin || isTeacherView);

  const readOnly = isJoinMode;

  /**
   * =========================================================
   * ✅ LOAD TEACHERS
   * =========================================================
   */

  useEffect(() => {
    const loadUsers = async () => {
      const data = await appService.getUsers();
      setTeachers(data);
    };

    loadUsers();
  }, []);

  /**
   * =========================================================
   * ✅ INIT VALUES
   * =========================================================
   */

  useEffect(() => {
    if (!angebot) {
      setTitle("");
      setSpecialTitle("");
      setOwners([]);
    }

    setReserveFullDay(false);
    setReserveFullWeek(false);

    if (angebot) {
      setTitle(angebot.title || "");
      setSpecialTitle(angebot.specialTitle || "");

      if (angebot.owners && teachers.length > 0) {
        const selected = teachers.filter((t) =>
          angebot.owners.some((o) => o.id === t.id),
        );

        setOwners(selected);
      } else {
        setOwners([]);
      }
    }
  }, [angebot, teachers, open]);

  /**
   * =========================================================
   * ✅ SAVE (RESERVE / EDIT)
   * =========================================================
   */

  const handleSave = async () => {
    if (!title.trim()) {
      setError("Titel fehlt.");
      return;
    }

    const data = {
      id: angebot?.id,
      title,
      specialTitle: specialTitle.trim(),
      owners: owners.map((u) => u.id),
      klasseId,
      halbtag,
      reserveFullDay,
      reserveFullWeek,
    };

    try {
      setError("");
      await onSave(data);
    } catch (err) {
      console.error(err);
      setError("Fehler beim Speichern.");
    }
  };

  /**
   * =========================================================
   * ✅ JOIN ANGEBOT
   * =========================================================
   */

  const handleJoin = async () => {
    try {
      await onSave({
        id: angebot.id,
        title: angebot.title,
        specialTitle: angebot.specialTitle || "",
        owners: [currentUser.id], //new user will be added!
        klasseId,
        halbtag,
        reserveFullDay,
        reserveFullWeek,
        joinMode: true,
      });
    } catch (err) {
      console.error(err);
      setError("Fehler beim Mitmachen.");
    }
  };

  /**
   * =========================================================
   * ✅ UNRESERVE
   * =========================================================
   */

  const handleUnreserve = async () => {
    if (!angebot?.id) return;

    try {
      await onSave({
        id: angebot.id,
        title: angebot.title || "",
        specialTitle: angebot.specialTitle || "",
        owners: [],
        klasseId,
        halbtag,
        reserveFullDay,
        reserveFullWeek,
      });
    } catch (err) {
      console.error(err);
      setError("Fehler beim Freigeben.");
    }
  };

  /**
   * =========================================================
   * ✅ DELETE
   * =========================================================
   */

  const handleDelete = async () => {
    if (!angebot?.id) return;

    try {
      await onDelete(angebot);
      onClose();
    } catch (err) {
      console.error(err);
      setError("Fehler beim Löschen.");
    }
  };

  /**
   * =========================================================
   * ✅ UI
   * =========================================================
   */

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>
        {isJoinMode
          ? "Mich zum Angebot hinzufügen"
          : isEditMode
            ? "Angebot bearbeiten"
            : "Angebot erstellen"}
        {" – "}
        {klasseId} ({halbtag})
      </DialogTitle>

      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}

        {isJoinMode && (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Das Angebot wurde bereits reserviert. Du kannst dich als zusätzliche
            Lehrperson eintragen.
          </Typography>
        )}

        <Stack spacing={2} mt={1}>
          {!isJoinMode && (
            <TextField
              label="Spezialtitel für die Klasse"
              value={specialTitle}
              disabled={readOnly}
              onChange={(e) => setSpecialTitle(e.target.value)}
              fullWidth
            />
          )}

          <TextField
            label="Titel"
            value={title}
            disabled={readOnly}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
          />
          {!isJoinMode && (
            <Stack direction="row" spacing={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reserveFullDay}
                    disabled={readOnly || reserveFullWeek}
                    onChange={(e) => setReserveFullDay(e.target.checked)}
                  />
                }
                label={
                  isMobile
                    ? "Ganzer Tag"
                    : isReserved
                      ? "Ganzen Tag freigeben"
                      : "Ganzen Tag reservieren"
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={reserveFullWeek}
                    disabled={readOnly || reserveFullDay}
                    onChange={(e) => setReserveFullWeek(e.target.checked)}
                  />
                }
                label={
                  isMobile
                    ? "Ganze Woche"
                    : isReserved
                      ? "Ganze Woche freigeben"
                      : "Ganze Woche reservieren"
                }
              />
            </Stack>
          )}

          <Autocomplete
            multiple
            disabled={readOnly}
            options={teachers}
            getOptionLabel={(u) =>
              `${u.email}${u.abbreviation ? ` (${u.abbreviation})` : ""}`
            }
            value={owners}
            onChange={(e, newValue) => setOwners(newValue)}
            filterSelectedOptions
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label="Lehrperson(en)" sx={{ mt: 2 }} />
            )}
          />

          {isJoinMode && (
            <Stack direction="row" spacing={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reserveFullDay}
                    disabled={!readOnly || reserveFullWeek}
                    onChange={(e) => setReserveFullDay(e.target.checked)}
                  />
                }
                label={"Ganzen Tag mitmachen"}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={reserveFullWeek}
                    disabled={!readOnly || reserveFullDay}
                    onChange={(e) => setReserveFullWeek(e.target.checked)}
                  />
                }
                label={"Ganze Woche mitmachen"}
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>

      {isJoinMode ? (
        <KsbDialogActions
          onCancel={onClose}
          onConfirm={handleJoin}
          confirmLabel="Mitmachen"
        />
      ) : (
        <KsbDialogActions
          onCancel={onClose}
          onConfirm={handleSave}
          confirmLabel={isEditMode ? "Speichern" : "Erstellen"}
          dangerAction={
            isASondertageEventAdmin && angebot ? handleDelete : null
          }
          dangerLabel={isASondertageEventAdmin && angebot ? "Löschen" : null}
          secondaryAction={isReserved ? handleUnreserve : null}
          secondaryLabel={isReserved ? "Freigeben" : null}
        />
      )}
    </Dialog>
  );
}
